import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Edit2, Check, X, Loader2, Copy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

interface EditableWorkspaceNameProps {
  workspaceId: string;
  currentName: string;
  userId: string;
  onNameUpdate: (newName: string) => void;
  className?: string;
}

const EditableWorkspaceName: React.FC<EditableWorkspaceNameProps> = ({
  workspaceId,
  currentName,
  userId,
  onNameUpdate,
  className = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(currentName);
  const [isUpdating, setIsUpdating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Reset edit value when currentName changes
  useEffect(() => {
    setEditValue(currentName);
  }, [currentName]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(currentName);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(currentName);
  };

  const handleCopyName = async () => {
    try {
      await navigator.clipboard.writeText(currentName);
      toast.success('Workspace name copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy workspace name');
    }
  };

  const handleNameClick = () => {
    if (!isEditing) {
      handleStartEdit();
    }
  };

  const handleSaveEdit = async () => {
    const trimmedName = editValue.trim();
    
    // Validation
    if (!trimmedName) {
      toast.error('Workspace name cannot be empty');
      return;
    }

    if (trimmedName === currentName) {
      setIsEditing(false);
      return;
    }

    if (trimmedName.length > 100) {
      toast.error('Workspace name cannot exceed 100 characters');
      return;
    }

    setIsUpdating(true);

    try {
      const { error } = await supabase
        .from('workspaces')
        .update({ 
          name: trimmedName,
          updated_at: new Date().toISOString()
        })
        .eq('id', workspaceId)
        .eq('user_id', userId);

      if (error) throw error;

      // Update parent component
      onNameUpdate(trimmedName);
      setIsEditing(false);
      
      toast.success('Workspace name updated successfully');
    } catch (error: any) {
      console.error('Error updating workspace name:', error);
      toast.error(error.message || 'Failed to update workspace name');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter workspace name"
          className="text-2xl md:text-3xl font-bold tracking-tight h-auto py-1 px-2 border-primary"
          disabled={isUpdating}
          maxLength={100}
        />
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={handleSaveEdit}
            disabled={isUpdating}
            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            {isUpdating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleCancelEdit}
            disabled={isUpdating}
            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 group ${className}`}>
      <h1 
        className="text-2xl md:text-3xl font-bold tracking-tight text-foreground cursor-pointer hover:text-primary transition-colors"
        onClick={handleNameClick}
        title="Click to edit workspace name"
      >
        {currentName}
      </h1>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="icon"
          variant="ghost"
          onClick={handleCopyName}
          className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent"
          title="Copy workspace name"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleStartEdit}
          className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent"
          title="Edit workspace name"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default EditableWorkspaceName;